// Validación de las IPs permitidas de un usuario de API. Espeja las reglas del rbac (ipaddr.js):
// solo unicast público, IPv4 con prefijo ≥ /16, IPv6 ≥ /48, máximo 20 entradas, sin comodines.

export const API_USER_IP_MAX_ENTRIES = 20;
export const API_USER_IPV4_MIN_PREFIX = 16;
export const API_USER_IPV6_MIN_PREFIX = 48;

type Family = 'ipv4' | 'ipv6';

interface Cidr {
    family: Family;
    bits: bigint;
    prefix: number;
}

const WIDTH: Record<Family, number> = { ipv4: 32, ipv6: 128 };

// Rangos especiales de ipaddr.js 2.x (todo lo que no es `unicast`), por familia.
const SPECIAL_RANGES: Record<Family, [string, number][]> = {
    ipv4: [
        ['0.0.0.0', 8],
        ['255.255.255.255', 32],
        ['224.0.0.0', 4],
        ['169.254.0.0', 16],
        ['127.0.0.0', 8],
        ['100.64.0.0', 10],
        ['10.0.0.0', 8],
        ['172.16.0.0', 12],
        ['192.168.0.0', 16],
        ['192.0.0.0', 24],
        ['192.0.2.0', 24],
        ['192.88.99.0', 24],
        ['198.18.0.0', 15],
        ['198.51.100.0', 24],
        ['203.0.113.0', 24],
        ['240.0.0.0', 4],
        ['192.175.48.0', 24],
        ['192.31.196.0', 24],
        ['192.52.193.0', 24],
    ],
    ipv6: [
        ['::', 128],
        ['fe80::', 10],
        ['ff00::', 8],
        ['::1', 128],
        ['fc00::', 7],
        ['::ffff:0:0', 96],
        ['fec0::', 10],
        ['100::', 64],
        ['::ffff:0:0:0', 96],
        ['64:ff9b::', 96],
        ['64:ff9b:1::', 48],
        ['2002::', 16],
        ['2001::', 32],
        ['2001:2::', 48],
        ['2001:3::', 32],
        ['2001:4:112::', 48],
        ['2620:4f:8000::', 48],
        ['2001:10::', 28],
        ['2001:20::', 28],
        ['2001:30::', 28],
        ['5f00::', 16],
        ['2001::', 23],
        ['2001:db8::', 32],
        ['3fff::', 20],
    ],
};

function parseIpv4(text: string): bigint | null {
    const parts = text.split('.');
    if (parts.length !== 4) return null;
    let value = BigInt(0);
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part) || /^0\d/.test(part)) return null;
        const octet = Number(part);
        if (octet > 255) return null;
        value = (value << BigInt(8)) | BigInt(octet);
    }
    return value;
}

function parseHexGroups(text: string): number[] | null {
    if (text === '') return [];
    const groups: number[] = [];
    const parts = text.split(':');
    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i];
        // Una IPv4 incrustada solo puede ir al final (p. ej. ::ffff:1.2.3.4).
        if (i === parts.length - 1 && part.includes('.')) {
            const v4 = parseIpv4(part);
            if (v4 === null) return null;
            groups.push(Number(v4 >> BigInt(16)), Number(v4 & BigInt(0xffff)));
            continue;
        }
        if (!/^[0-9a-fA-F]{1,4}$/.test(part)) return null;
        groups.push(parseInt(part, 16));
    }
    return groups;
}

function parseIpv6(text: string): bigint | null {
    const halves = text.split('::');
    if (halves.length > 2) return null;
    const head = parseHexGroups(halves[0]);
    const tail = halves.length === 2 ? parseHexGroups(halves[1]) : [];
    if (!head || !tail) return null;
    const missing = 8 - head.length - tail.length;
    if (halves.length === 2 ? missing < 1 : missing !== 0) return null;
    const groups = [...head, ...Array<number>(Math.max(missing, 0)).fill(0), ...tail];
    return groups.reduce((acc, g) => (acc << BigInt(16)) | BigInt(g), BigInt(0));
}

/** IP suelta (⇒ /32 o /128) o CIDR. null si el texto no es una IP válida. */
function parseCidr(entry: string): Cidr | null {
    const parts = entry.split('/');
    if (parts.length > 2) return null;
    const address = parts[0];
    const family: Family = address.includes(':') ? 'ipv6' : 'ipv4';
    const bits = family === 'ipv4' ? parseIpv4(address) : parseIpv6(address);
    if (bits === null) return null;
    if (parts.length === 1) return { family, bits, prefix: WIDTH[family] };
    const prefixText = parts[1];
    if (!/^\d{1,3}$/.test(prefixText) || /^0\d/.test(prefixText)) return null;
    const prefix = Number(prefixText);
    return prefix > WIDTH[family] ? null : { family, bits, prefix };
}

function sharesNetwork(a: Cidr, b: Cidr): boolean {
    const shift = BigInt(WIDTH[a.family] - Math.min(a.prefix, b.prefix));
    return a.bits >> shift === b.bits >> shift;
}

function overlapsSpecialRange(cidr: Cidr): boolean {
    return SPECIAL_RANGES[cidr.family].some(([address, prefix]) => {
        const special = parseCidr(`${address}/${String(prefix)}`);
        return special !== null && sharesNetwork(cidr, special);
    });
}

/** Separa el texto del campo en entradas: una por línea (también acepta comas). */
export function parseIpAllowlist(raw: string): string[] {
    return raw
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
}

function entryError(entry: string): string | null {
    if (entry.includes('*')) return 'No se permite el comodín *: escribe IPs o rangos concretos.';
    const cidr = parseCidr(entry);
    if (!cidr) {
        return `Formato inválido en "${entry}": usa una IP como 201.20.30.40 o un rango como 189.128.0.0/16.`;
    }
    const minPrefix = cidr.family === 'ipv4' ? API_USER_IPV4_MIN_PREFIX : API_USER_IPV6_MIN_PREFIX;
    if (cidr.prefix < minPrefix) {
        return `Rango demasiado amplio en "${entry}": usa /${String(minPrefix)} o más chico.`;
    }
    if (overlapsSpecialRange(cidr)) {
        return `"${entry}" no es una IP pública: usa la IP de salida a internet de tu servidor.`;
    }
    return null;
}

/** Primer motivo por el que la lista no es válida, o null si el backend la aceptaría. */
export function apiUserIpAllowlistError(raw: string): string | null {
    const entries = parseIpAllowlist(raw);
    if (entries.length === 0) return 'Agrega al menos una IP o rango permitido.';
    if (entries.length > API_USER_IP_MAX_ENTRIES) {
        return `Máximo ${String(API_USER_IP_MAX_ENTRIES)} IPs o rangos.`;
    }
    for (const entry of entries) {
        const error = entryError(entry);
        if (error) return error;
    }
    return null;
}
